/**
 * Feb 2026 · 8 Sep 2026 · User-Directive „Objekt-Umschalter global synchron":
 *   „Das aktiv gewählte Objekt soll global im gesamten Kunden-Bereich
 *    synchronisiert sein. […] Alle anderen Bereiche — insbesondere
 *    Einstellungen — sollen automatisch auf dasselbe Objekt umschalten."
 *
 * Wiederverwendbare Tab-Bar für den Objekt-Umschalter. Consumer-Seiten
 * (Dashboard, Settings, Family, ...) mounten sie oben in ihrem Content und
 * bekommen automatisch den Context-State. Rendert nichts, wenn der Kunde
 * nur ein Objekt besitzt.
 */
import { useI18n } from "../../lib/i18n";
import { useProperty } from "../../lib/PropertyContext";
import { Snowflake, Sun, CircleDot, Star } from "lucide-react";
import { BRAND_COLORS, withAlpha } from "../../lib/bookingStatus";

function SeasonIcon({ season, active }) {
  // Feb 2026 · 8 Sep 2026 · User-Directive „Objekt-Saison-Label": Kleines
  // Icon neben dem Objektnamen im Switcher. Winter → Snowflake, Sommer → Sun,
  // year_round → dezenter Kreis-Dot.
  if (!season) return null;
  const color = active ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.5);
  if (season === "winter") return <Snowflake className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color }} />;
  if (season === "summer") return <Sun className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color }} />;
  if (season === "year_round") return <CircleDot className="w-3.5 h-3.5" strokeWidth={1.6} style={{ color }} />;
  return null;
}

export default function PropertySwitcher({ testidPrefix = "property-switcher", label }) {
  const { properties, selected, setSelectedProperty } = useProperty();
  const { lang } = useI18n();
  const de = lang === "de";
  if (!properties || properties.length <= 1) return null;
  return (
    <div className="mb-4" data-testid={testidPrefix}>
      <div className="label-caps mb-3" style={{ color: BRAND_COLORS.gold }}>
        {label || (de ? "Ihre Objekte" : "Your properties")}
      </div>
      <div className="flex flex-wrap gap-2">
        {properties.map((p) => {
          const active = selected?.id === p.id;
          // Feb 2026 · 9 Sep 2026 · User-Directive „Hauptobjekt goldener
          // Rahmen": Das Hauptobjekt wird IMMER mit einem goldenen 2-px-
          // Rahmen hervorgehoben — unabhängig davon, ob es gerade aktiv
          // ist oder nicht. Zusätzlich ein kleiner Stern am Anfang der
          // Tab, damit die visuelle Zuordnung eindeutig bleibt.
          const primary = !!p.is_primary;
          const borderColor = primary
            ? BRAND_COLORS.gold
            : active ? BRAND_COLORS.navy : withAlpha(BRAND_COLORS.navy, 0.15);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setSelectedProperty(p)}
              data-testid={`${testidPrefix}-tab-${p.id}`}
              data-active={active ? "true" : "false"}
              data-primary={primary ? "true" : "false"}
              className={`inline-flex items-center gap-2 px-4 py-2 text-[12px] tracking-[0.08em] uppercase transition-colors ${
                active
                  ? "text-ivory"
                  : "bg-white text-navy-dark/70 hover:text-navy hover:bg-navy/5"
              }`}
              style={{
                background: active ? BRAND_COLORS.navy : undefined,
                border: `2px solid ${borderColor}`,
              }}
              title={primary ? (de ? "Hauptobjekt" : "Primary property") : undefined}
            >
              {primary && (
                <Star
                  className="w-3.5 h-3.5"
                  strokeWidth={1.6}
                  style={{ color: BRAND_COLORS.gold }}
                  fill={BRAND_COLORS.gold}
                />
              )}
              <SeasonIcon season={p.season} active={active} />
              {!primary && !p.season && (
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ background: active ? BRAND_COLORS.gold : withAlpha(BRAND_COLORS.navy, 0.35) }}
                />
              )}
              {p.name}
            </button>
          );
        })}
      </div>
    </div>
  );
}
